import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Footprints, Droplet, CalendarPlus, Video, ArrowRight, Heart } from "lucide-react";
import AppShell from "@/components/AppShell";
import RingChart from "@/components/RingChart";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const fmt = (iso) =>
  new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export default function ClientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [next, setNext] = useState(null);
  const [stress, setStress] = useState(3);

  useEffect(() => {
    api.get("/bookings").then((r) => {
      const upcoming = r.data
        .filter((b) => b.status === "confirmed" && new Date(b.startTime) > new Date())
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      setNext(upcoming[0] || null);
    }).catch(() => {});
  }, []);

  return (
    <AppShell active="dashboard">
      <section className="mb-12">
        <h1 className="text-5xl sm:text-6xl font-headline font-light text-primary tracking-tight leading-[1.05]">
          {greeting()},<br />{user?.firstName}
        </h1>
        <p className="mt-4 text-on-surface-variant tracking-wide">Take a moment to center yourself today.</p>
      </section>

      {/* Next session + quick book */}
      <section className="mb-12 grid md:grid-cols-2 gap-6">
        <div className="bg-surface-container-lowest rounded-3xl p-7 shadow-ambient flex flex-col justify-between" data-testid="next-session-card">
          <div>
            <h3 className="text-xs font-label uppercase tracking-widest text-outline">Next session</h3>
            {next ? (
              <>
                <p className="mt-3 font-headline text-2xl text-primary">{next.sessionType}</p>
                <p className="mt-1 text-on-surface-variant text-sm">{fmt(next.startTime)}</p>
              </>
            ) : (
              <>
                <p className="mt-3 font-headline text-2xl text-primary">No sessions yet</p>
                <p className="mt-1 text-on-surface-variant text-sm">Book your first session with your coach.</p>
              </>
            )}
          </div>
          <div className="mt-6 flex gap-3">
            {next ? (
              <a href={next.meetingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary-fixed text-primary-on-container text-sm font-medium hover:opacity-90 transition-opacity duration-300" data-testid="join-session-button">
                <Video className="w-4 h-4" strokeWidth={1.5} /> Join
              </a>
            ) : null}
            <button onClick={() => navigate("/app/book")} className="inline-flex items-center gap-2 px-5 py-3 rounded-full zenith-cta text-white text-sm font-medium hover:opacity-90 transition-opacity duration-300" data-testid="dashboard-book-button">
              <CalendarPlus className="w-4 h-4" strokeWidth={1.5} /> Book a session
            </button>
          </div>
        </div>

        {/* Stress check-in */}
        <div className="bg-surface-container-low rounded-3xl p-7 flex flex-col justify-between" data-testid="stress-widget">
          <div>
            <h3 className="text-xs font-label uppercase tracking-widest text-outline">Check-in</h3>
            <p className="text-2xl font-headline text-on-surface mt-1">How is your stress level?</p>
          </div>
          <div className="mt-8">
            <input type="range" min="1" max="10" value={stress} onChange={(e) => setStress(e.target.value)} className="w-full h-1.5 bg-surface-container-highest rounded-full appearance-none cursor-pointer accent-primary" data-testid="stress-slider" />
            <div className="flex justify-between mt-3 text-xs font-label text-outline">
              <span>Calm</span><span>High</span>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-3 text-secondary">
            <Heart className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-sm font-medium">Listening to your rhythm</span>
          </div>
        </div>
      </section>

      {/* Vitality rings */}
      <section className="mb-12">
        <h2 className="text-2xl font-headline text-primary mb-7">Daily Vitality</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { label: "Sleep", value: 78, display: "7h 20m", icon: <Moon className="w-5 h-5" strokeWidth={1.5} />, id: "g1" },
            { label: "Steps", value: 62, display: "6,432", icon: <Footprints className="w-5 h-5" strokeWidth={1.5} />, id: "g2" },
            { label: "Water", value: 48, display: "1.2L", icon: <Droplet className="w-5 h-5" strokeWidth={1.5} />, id: "g3" },
          ].map((m) => (
            <div key={m.label} className="bg-surface-container-lowest rounded-3xl p-6 flex flex-col items-center shadow-ambient" data-testid={`vitality-${m.label.toLowerCase()}`}>
              <RingChart value={m.value} icon={m.icon} gradientId={m.id} />
              <span className="mt-4 text-xs font-label text-outline uppercase tracking-wider">{m.label}</span>
              <span className="text-lg font-headline text-on-surface">{m.display}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Weekly ritual */}
      <section className="mb-8">
        <div className="glass-blush rounded-3xl p-8 flex items-center justify-between overflow-hidden relative shadow-ambient" data-testid="weekly-ritual-card">
          <div className="z-10">
            <h3 className="text-secondary-on-container font-headline text-xl mb-2">Weekly Ritual</h3>
            <p className="text-secondary-on-container/80 max-w-[220px] text-sm leading-relaxed">You've reached your mindfulness goal 4 days in a row. Keep breathing.</p>
          </div>
          <button onClick={() => navigate("/app/chat")} className="z-10 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-surface-container-lowest text-secondary text-sm font-medium hover:opacity-90 transition-opacity duration-300" data-testid="ritual-chat-button">
            Chat <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </section>
    </AppShell>
  );
}
